import { Typography, Grid, Alert, Button, Box, Paper } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Control, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Order, PaymentOrderState } from "../../models/order";
import AutocompleteInputOption from "../../models/autocompleteInputOption";
import AppTextInput from "../AppTextInput";
import AppDatePicker from "../AppDatePicker";
import AppSelect from "../AppSelect";
import { OrderFormValues } from "../../models/orderFormValues";
import AppAutocompleteWithDraft from "../AppAutocompleteWithDraft";
import agent from "../../api/agent";
import OrderSelectionModal from "./OrderSelectionModal";
import ServiceChangeDialog from "./ServiceChangeDialog";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { fetchRecentAdminOrdersAsync, selectRecentAdminOrders, selectRecentAdminOrdersLoading, clearRecentAdminOrders } from "../../../features/admin/orders/adminOrdersSlice";
import { fetchRecentClientOrdersAsync, selectRecentClientOrders, selectRecentClientOrdersLoading, clearRecentClientOrders } from "../../../features/client/orders/clientOrdersSlice";
import { OPENAI_QUOTA_ERROR_CODE } from "../../constants/openAI";
import OpenAIQuotaToastContent from "../OpenAIQuotaToastContent";

interface Props {
    order: Order | undefined;
    control: Control<OrderFormValues> | undefined;
    possibleServices: AutocompleteInputOption[];
    watch: UseFormWatch<OrderFormValues>;
    setValue: UseFormSetValue<OrderFormValues>;
    handleOpenModal: () => void;
    setManualServiceChange: () => void;
    isAdmin: boolean;
    setEducationEffects?: (effects: string[]) => void;
    onCopyTestsDecision?: (copyFromServiceId: number | null, overwriteTestMaterials: boolean) => void;
}

export default function OrderFormBasicInformation({ order, control, possibleServices, watch, setValue, handleOpenModal, setManualServiceChange, isAdmin, setEducationEffects, onCopyTestsDecision }: Props) {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const selectedServiceId = watch('serviceId');
    const customExamName = watch('customeClientServiceName');
    const trainingProgram = watch('trainingProgram');
    const [serviceHasNoTestPath, setServiceHasNoTestPath] = useState<boolean>(false);
    const [serviceHasNoKeyPath, setServiceHasNoKeyPath] = useState<boolean>(false);
    const [selectedService, setSelectedService] = useState<AutocompleteInputOption | undefined>(undefined);
    const [generatingEffects, setGeneratingEffects] = useState<boolean>(false);
    const [expandedEffectsSection, setExpandedEffectsSection] = useState<'ai' | null>(null);
    const [openOrderSelectionModal, setOpenOrderSelectionModal] = useState(false);
    const [orderSkipCount, setOrderSkipCount] = useState(0);
    
    // State for service change dialog
    const [openServiceChangeDialog, setOpenServiceChangeDialog] = useState(false);
    const [originalServiceId] = useState<number | undefined>(order?.serviceId);
    const [originalService, setOriginalService] = useState<AutocompleteInputOption | undefined>(undefined);
    const [pendingNewService, setPendingNewService] = useState<AutocompleteInputOption | undefined>(undefined);

    // Redux selectors for recent orders
    const recentAdminOrders = useAppSelector(selectRecentAdminOrders);
    const recentAdminOrdersLoading = useAppSelector(selectRecentAdminOrdersLoading);
    const recentClientOrders = useAppSelector(selectRecentClientOrders);
    const recentClientOrdersLoading = useAppSelector(selectRecentClientOrdersLoading);
    const user = useAppSelector(state => state.account.user);
    const isSuperAdmin = !!user?.roles?.includes('SuperAdmin');

    const recentOrders = isAdmin ? recentAdminOrders : recentClientOrders;
    const recentOrdersLoading = isAdmin ? recentAdminOrdersLoading : recentClientOrdersLoading;
    
    // Ref to track if component is mounted (for async operations)
    const isMountedRef = useRef<boolean>(true);
    // Ref for AbortController to cancel ongoing requests
    const abortControllerRef = useRef<AbortController | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Cancel any ongoing AI generation request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Check if AI generation should be available
    const serviceName = selectedService?.name || '';
    const canGenerateAIEffects = customExamName && 
        trainingProgram && 
        customExamName.trim() !== '' && 
        trainingProgram.trim() !== '' &&
        customExamName.trim().toLowerCase() !== serviceName.trim().toLowerCase();

    // Zapisz oryginalną usługę gdy possibleServices się załadują
    useEffect(() => {
        if (order && originalServiceId && possibleServices.length > 0 && !originalService) {
            // Konwertujemy na Number bo originalServiceId może być string
            const origService = possibleServices.find(s => s.id === Number(originalServiceId));
            if (origService) {
                setOriginalService(origService);
            }
        }
    }, [order, originalServiceId, possibleServices, originalService]);

    useEffect(() => {
        const service = possibleServices.find(service => service.id === selectedServiceId);
        setSelectedService(service);

        if (service) {
            // Sprawdź typ usługi i odpowiedni atrybut
            if (service.isGraphical) {
                // Dla usług graficznych sprawdź packageTestPath i packageTestKeyPath
                setServiceHasNoTestPath(!service.packageTestPath);
                setServiceHasNoKeyPath(!service.packageTestKeyPath);
            } else {
                // Dla usług niegraficznych sprawdź questionsJson (zawiera zarówno pytania jak i odpowiedzi)
                setServiceHasNoTestPath(!service.questionsJson);
                setServiceHasNoKeyPath(!service.questionsJson);
            }
            
            // Pokaż dialog zmiany usługi gdy admin zmienia usługę w istniejącym zamówieniu
            // Porównujemy numerycznie aby uniknąć problemów z typami string/number
            const serviceChanged = originalServiceId && Number(selectedServiceId) !== Number(originalServiceId);
            
            if (order && isAdmin && serviceChanged && service) {
                // Użyj originalService ze state lub znajdź w possibleServices
                // Konwertujemy na Number bo originalServiceId może być string
                const origService = originalService || possibleServices.find(s => s.id === Number(originalServiceId));
                if (origService) {
                    if (!originalService) {
                        setOriginalService(origService);
                    }
                    setPendingNewService(service);
                    setOpenServiceChangeDialog(true);
                }
            }
        } else {
            // Brak wybranej usługi
            setServiceHasNoTestPath(false);
            setServiceHasNoKeyPath(false);
        }
    }, [selectedServiceId, possibleServices, order, isAdmin, originalServiceId, originalService]);

    const paymentOrderStateLabels = {
        [PaymentOrderState.PENDING]: 'Oczekujące',
        [PaymentOrderState.WAITING_FOR_CONFIRMATION]: 'Oczekuje na potwierdzenie',
        [PaymentOrderState.COMPLETED]: 'Zakończone',
        [PaymentOrderState.CANCELED]: 'Anulowane',
    };

    const paymentOrderStateOptions = Object.entries(PaymentOrderState)
        .filter(([, value]) => typeof value === 'number')
        .map(([, value]) => ({ label: paymentOrderStateLabels[value as PaymentOrderState], value: value as number }));

    const isPathAdminOrders = location.pathname.startsWith('/admin/orders');

    const handleGenerateAIEffects = async () => {
        if (!customExamName || !trainingProgram) {
            toast.warning('Wypełnij nazwę egzaminu i program szkolenia przed generowaniem efektów.');
            return;
        }

        // Cancel any previous ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        setGeneratingEffects(true);
        try {
            const response = await agent.AI.generateEducationEffects(
                {
                    customExamName: customExamName.trim(),
                    trainingProgram: trainingProgram.trim()
                },
                abortControllerRef.current.signal
            );

            // Check if component is still mounted before updating state
            if (!isMountedRef.current) {
                return;
            }

            if (response.effects && response.effects.length > 0) {
                // Join effects with semicolon and set to form
                const effectsString = response.effects.join(';');
                setValue('educationEffects', effectsString, { shouldDirty: true });
                toast.success(`Wygenerowano ${response.effects.length} efektów kształcenia. Możesz je teraz przejrzeć i edytować.`);
                // Open modal to allow user to review/edit
                handleOpenModal();
            } else {
                toast.warning('Nie udało się wygenerować efektów kształcenia. Spróbuj ponownie.');
            }
        } catch (error: any) {
            // Don't show error if request was cancelled (user navigated away)
            if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
                console.log('AI generation request was cancelled');
                return;
            }
            
            // Check if component is still mounted before showing error
            if (!isMountedRef.current) {
                return;
            }
            
            console.error('Error generating education effects:', error);
            const errorMessage = error?.data?.error || error?.message || 'Wystąpił błąd podczas generowania efektów kształcenia. Spróbuj ponownie.';
            if (error?.data?.code === OPENAI_QUOTA_ERROR_CODE && isSuperAdmin) {
                toast.error(<OpenAIQuotaToastContent message={errorMessage} billingUrl={error.data.billingUrl} />, {
                    autoClose: false,
                    toastId: 'openai-quota-exhausted'
                });
            } else if (error?.data?.code === OPENAI_QUOTA_ERROR_CODE) {
                toast.error('Nie udało się wygenerować efektów kształcenia. Spróbuj ponownie później.');
            } else {
                toast.error(errorMessage);
            }
        } finally {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setGeneratingEffects(false);
            }
        }
    };

    // Handler to open order selection modal
    const handleOpenOrderSelectionModal = () => {
        setOpenOrderSelectionModal(true);
        setOrderSkipCount(0); // Reset skip count when opening modal
        // Clear previous orders and fetch fresh ones when modal is opened
        if (isAdmin) {
            dispatch(clearRecentAdminOrders());
            dispatch(fetchRecentAdminOrdersAsync(0));
        } else {
            dispatch(clearRecentClientOrders());
            dispatch(fetchRecentClientOrdersAsync(0));
        }
    };

    // Handler to load more orders
    const handleLoadMoreOrders = () => {
        const newSkip = orderSkipCount + 10;
        console.log('handleLoadMoreOrders called, current orderSkipCount:', orderSkipCount, 'newSkip:', newSkip);
        setOrderSkipCount(newSkip);
        if (isAdmin) {
            dispatch(fetchRecentAdminOrdersAsync(newSkip));
        } else {
            dispatch(fetchRecentClientOrdersAsync(newSkip));
        }
    };

    // Handler to select order from modal
    // Handler to select order from modal
    const handleSelectOrderFromModal = async (publicId: number) => {
        try {
            const endpoint = isAdmin 
                ? agent.Admin.Orders.getEducationEffectsFromOrder(publicId)
                : agent.Client.Orders.getEducationEffectsFromOrder(publicId);
            
            const effectsText = await endpoint;
            
            if (effectsText) {
                setValue('educationEffects', effectsText, { shouldValidate: true, shouldDirty: true });
                if (setEducationEffects) {
                    setEducationEffects(effectsText.split(';').filter((e: string) => e.trim()));
                }
                toast.success('Pobrano efekty uczenia się z zamówienia ' + publicId);
            }
        } catch (error) {
            console.log(error);
            toast.error('Nie udało się pobrać efektów uczenia się.');
        }
    };

    return (
        <>
            <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>Podstawowe dane o egzaminie</Typography>
            <Grid container spacing={2} marginBottom={3}>
                {order && (
                    <>
                        <Grid item xs={12}>
                            <AppSelect<OrderFormValues>
                                control={control}
                                name='paymentOrderState'
                                label='Stan płatności'
                                items={paymentOrderStateOptions}
                                disabled={!isPathAdminOrders}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="h6">Nr zamówienia: {order.publicId}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="h6">Zamawiający: {order.userDisplayName}</Typography>
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>
                    <AppAutocompleteWithDraft<OrderFormValues>
                        control={control}
                        name='serviceId'
                        label='Wskaż egzamin'
                        items={possibleServices}
                        selectedids={order?.serviceId.toString()}
                        onChange={setManualServiceChange}
                    />
                </Grid>
                {serviceHasNoTestPath && isAdmin && (
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <Alert severity="error">
                            {selectedService && selectedService.isGraphical
                                ? "Wybrana usługa nie posiada wzoru arkusza egzaminacyjnego. Udostępnienie testów będzie możliwe po dodaniu arkusza do odpowiedniej usługi."
                                : "Wybrana usługa nie posiada pytań egzaminacyjnych. Udostępnienie testów będzie możliwe po dodaniu pytań do odpowiedniej usługi."}
                        </Alert>
                    </Grid>
                )}
                {!serviceHasNoTestPath && serviceHasNoKeyPath && isAdmin && selectedService?.isGraphical && (
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <Alert severity="warning">
                            Wybrana usługa posiada arkusz egzaminacyjny, ale nie posiada klucza odpowiedzi. Udostępnienie klucza będzie możliwe po dodaniu go do odpowiedniej usługi.
                        </Alert>
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={3} marginBottom={3}>
                <Grid item xs={12}>
                    <AppTextInput control={control} name='customeClientServiceName' label='Nazwa egzaminu, jaka ma pojawić się na certyfikatach i protokołach' />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AppDatePicker control={control} name='preposedDateTime' label='Proponowana data realizacji' disabled={false} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AppTextInput control={control} name='placeOfServiceAndAnotherInformations' label='Miejsce realizacji (ulica, nr budynku/lokalu, miejscowość)' />
                </Grid>
                <Grid item xs={12}>
                    <AppTextInput control={control} name='clientComment' label='Twój komentarz/uwagi' />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <AppTextInput 
                        control={control} 
                        name='trainingProgram' 
                        label='Wprowadź program szkolenia, na podstawie którego przygotujemy bądź potwierdzimy stosowne efekty uczenia się' 
                        multiline 
                        minRows={3}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box display='flex' gap={2} justifyContent='center' flexWrap='wrap'>
                                {isAdmin && (
                                    <Box display='flex' gap={1}>
                                        <Button
                                            variant='outlined'
                                            color='primary'
                                            size='small'
                                            onClick={handleOpenOrderSelectionModal}
                                        >
                                            Pobierz efekty kształcenia z istniejącego zamówienia
                                        </Button>
                                    </Box>
                                )}
                                {!isAdmin && (
                                    <Box display='flex' gap={1}>
                                        <Button
                                            variant='outlined'
                                            color='primary'
                                            size='small'
                                            onClick={handleOpenOrderSelectionModal}
                                        >
                                            Pobierz efekty z Twoich zamówień
                                        </Button>
                                    </Box>
                                )}
                                <Button
                                    disabled={!canGenerateAIEffects}
                                    variant={expandedEffectsSection === 'ai' ? 'contained' : 'outlined'}
                                    color='primary'
                                    size='small'
                                    startIcon={<AutoAwesomeIcon />}
                                    onClick={() => setExpandedEffectsSection(expandedEffectsSection === 'ai' ? null : 'ai')}
                                >
                                    Wygeneruj efekty kształcenia przy pomocy AI
                                </Button>
                            </Box>
                        </Grid>
                        {expandedEffectsSection === 'ai' && canGenerateAIEffects && (
                                <Grid item xs={12}>
                                    <Box
                                        component={Paper}
                                        sx={{ 
                                            p: 2,
                                            backgroundColor: 'secondary',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ mb: 1.5 }}>
                                            Możesz automatycznie wygenerować 15 efektów kształcenia dopasowanych do Twojego programu szkolenia przy pomocy sztucznej inteligencji. 
                                            Po wygenerowaniu będziesz mógł je przejrzeć, edytować lub usunąć poszczególne punkty.
                                        </Typography>
                                        <LoadingButton
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            loading={generatingEffects}
                                            loadingPosition="start"
                                            startIcon={<AutoAwesomeIcon />}
                                            onClick={handleGenerateAIEffects}
                                        >
                                            {generatingEffects ? 'Generowanie...' : 'Wygeneruj efekty AI'}
                                        </LoadingButton>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                <Grid item xs={12}>
                    <Alert severity="info">Edytuj poniższe pole w sytuacji, gdy oczekujesz istotnie odmiennego opisu efektów kształcenia, niż widniejące we wzorze</Alert>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Typography variant="h5" sx={{ mt: 2 }}>Efekty kształcenia:</Typography>
                </Grid>
                <Grid item xs={12} sm={12}>
                    {watch('educationEffects') ? watch('educationEffects')?.split(';').map((effect: string, index: number) => (
                        <Typography key={index}>{index + 1}. {effect}</Typography>
                    )) : null}
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Button variant="outlined" onClick={handleOpenModal}>Edytuj Efekty Kształcenia</Button>
                </Grid>
            </Grid>

            <OrderSelectionModal
                open={openOrderSelectionModal}
                onClose={() => setOpenOrderSelectionModal(false)}
                recentOrders={recentOrders}
                loading={recentOrdersLoading}
                onSelectOrder={handleSelectOrderFromModal}
                onLoadMore={handleLoadMoreOrders}
                hasMore={true}
                isAdmin={isAdmin}
            />

            {/* Dialog potwierdzający zmianę usługi */}
            {order && pendingNewService && (
                <ServiceChangeDialog
                    open={openServiceChangeDialog}
                    onClose={() => setOpenServiceChangeDialog(false)}
                    order={order}
                    newService={pendingNewService}
                    originalService={originalService}
                    onConfirm={(copyFromServiceId, overwriteTestMaterials) => {
                        if (onCopyTestsDecision) {
                            onCopyTestsDecision(copyFromServiceId, overwriteTestMaterials);
                        }
                    }}
                    onCancel={() => {
                        // Przywróć oryginalną usługę w formularzu
                        if (originalServiceId) {
                            setValue('serviceId', originalServiceId);
                        }
                    }}
                />
            )}
        </>
    );
}
